import cl from 'classnames';
import * as React from 'react';
import { createEditor, Transforms } from 'slate';
import { withHistory } from 'slate-history';
import { ReactEditor, Slate, useSlateStatic, withReact } from 'slate-react';

import { pipe } from '../../slatePlugins/common/utils/pipe';
import {
  EditablePlugins,
  EditablePluginsProps,
} from '../../slatePlugins/components/core/EditablePlugins';
import renderNode, { Div } from '../renderNode';
import { EditorProps } from './types';
import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import styled from 'styled-components';
import { Portal } from '@reach/portal';
import { CSS } from '@dnd-kit/utilities';
import { colorsV2 } from '../colors-v2';

const DragOverlayMain = styled.div`
  .drag-handle {
    border: none;
    background-color: transparent;
    z-index: 2;
    font-size: 16px;
    line-height: 1;
  }
`;

const MainSortable = styled.div`
  position: relative;
  &.active {
    opacity: 0.3;
  }

  &:not(.active) {
    &.insert-before,
    &.insert-after {
      &::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 1.5px;
        background-color: ${colorsV2.blue40};
        left: -16px;
        right: 0;
      }
    }
  }

  &.insert-before {
    &::after {
      top: 0;
    }
  }

  &.insert-after {
    &::after {
      bottom: 0;
    }
  }

  .drag-handle {
    border: none;
    padding: 4px;
    position: absolute;
    background-color: transparent;
    z-index: 99;
    left: -20px;
    top: 6px;
    cursor: move;
    cursor: grab;
    font-size: 16px;
    line-height: 1;
  }
`;

export type CustomElement = {
  id?: string;
  type?: string;
  children: any;
  [key: string]: any;
  text?: any;
};
type FormattedText = { text: string; bold: boolean; italic: boolean; [key: string]: any };
type CustomText = FormattedText;
declare module 'slate' {
  interface CustomTypes {
    Editor: Record<string, any>;
    Element: CustomElement;
    Text: CustomText;
    Node: Record<string, any>;
  }
}

const withPlugins = [withReact, withHistory] as const;

const defaultSlateValue = [
  {
    type: 'p',
    children: [{ text: '' }],
  },
];

const INLINE_STYLE_OVERRIDE = {
  fontSize: 'inherit',
  lineHeight: 'inherit',
};

const SortableDragDropWrapper = ({ activeIndex, element, children, attributes }) => {
  const editor = useSlateStatic();
  const isTopLevel = React.useMemo(
    () => findTopLevelElement({ editor, element }),
    [editor, element]
  );
  const [onHover, setOnHover] = React.useState(false);
  const sortable = useSortable({
    id: element?.id,
    animateLayoutChanges: () => true,
  });

  const insertPosition = React.useMemo(() => {
    if (sortable?.over?.id === element?.id) {
      return sortable.index > activeIndex ? 'before' : 'after';
    }
    return null;
  }, [sortable, activeIndex, element]);
  if (!isTopLevel) {
    return children;
  }
  return (
    <div
      onMouseEnter={() => setOnHover(true)}
      onMouseLeave={() => setOnHover(false)}
      {...attributes}
    >
      <MainSortable
        className={cl({
          active: sortable.isDragging,
          'insert-before': insertPosition === 'before',
          'insert-after': insertPosition === 'after',
        })}
        style={
          {
            transition: sortable.transition,
            transform: sortable.isSorting ? undefined : CSS.Translate.toString(sortable.transform),
            // pointerEvents: sortable.isSorting ? 'none' : undefined,
          } as any
        }
        ref={sortable.setNodeRef}
      >
        <span className="drag-handle-wrapper">
          <button
            className="drag-handle"
            contentEditable={false}
            {...sortable.attributes}
            {...sortable.listeners}
            style={{
              opacity: onHover ? 1 : 0,
            }}
          >
            ⠿
          </button>
        </span>

        {children}
      </MainSortable>
    </div>
  );
};

const findTopLevelElement = ({ editor, element }) => {
  try {
    const path = ReactEditor.findPath(editor, element);
    return path.length === 1;
  } catch (e) {
    console.error(e);
  }
  return null;
};

export const DolEditable = React.forwardRef((props: EditablePluginsProps, ref) => {
  const editor = useSlateStatic();
  const [activeId, setActiveId] = React.useState(null);
  const activeElement = editor?.children?.find((x) => x.id === activeId);
  const handleDragStart = (event) => {
    if (event.active) {
      clearSelection();
      setActiveId(event.active.id);
    }
  };

  const handleDragEnd = (event) => {
    const overId = event.over?.id;
    const overIndex = editor.children.findIndex((x) => x.id === overId);

    if (overId !== activeId && overIndex !== -1) {
      Transforms.moveNodes(editor, {
        at: [],
        match: (node: any) => node.id === activeId,
        to: [overIndex],
      });
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const clearSelection = () => {
    ReactEditor.blur(editor as any);
    Transforms.deselect(editor);
    window.getSelection()?.empty();
  };

  const items = React.useMemo(
    () => (editor?.children || [])?.map((element) => element.id),
    [editor?.children]
  );
  const activeIndex = activeId ? items.indexOf(activeId) : -1;

  if (props.withDraggable) {
    return (
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        collisionDetection={closestCenter}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <EditablePlugins
            renderDragElement={(p) => <SortableDragDropWrapper activeIndex={activeIndex} {...p} />}
            ref={ref}
            style={INLINE_STYLE_OVERRIDE}
            {...props}
          />
        </SortableContext>
        <Portal>
          <DragOverlay>
            {activeElement && <DragOverlayContent element={activeElement} />}
          </DragOverlay>
        </Portal>
      </DndContext>
    );
  }

  return <EditablePlugins ref={ref} style={INLINE_STYLE_OVERRIDE} {...props} />;
});

const DolEditorMainWrapper = styled.div`
  div[role='textbox'] {
    outline: none;
  }
`;

const getInitialEditorValue = (initValue) => {
  if (initValue?.length > 0) {
    return initValue;
  }
  return defaultSlateValue;
};

export const DolEditorBaseContext = React.createContext(null);

export const DolEditor = React.forwardRef(
  (
    {
      initialValue,
      onEditorChange = () => {},
      withToolbar,
      onMounted = () => {},
      onClickHandler = () => {},
      children,
      WrapperComponent = Div,
      WrapperComponentProps = {},
      renderRight,
      className,
      editorClassName,
      editorChains = [],
      withCustomSpacing,
      forceValue = false,
      withDraggable = false,

      isReadOnly, // omit from DOM
      ...rest
    }: EditorProps,
    ref
  ) => {
    const [value, setValue] = React.useState(getInitialEditorValue(initialValue));
    const [editor, setEditor] = React.useState(
      pipe(createEditor(), ...[...withPlugins, ...editorChains])
    );

    React.useEffect(() => {
      onMounted(editor as any);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <DolEditorBaseContext.Provider value={{ editor, setEditor }}>
        <DolEditorMainWrapper
          className={cl('dol-editor', { 'custom-spacing': withCustomSpacing }, editorClassName)}
        >
          <Slate
            editor={editor}
            onChange={(newValue: any) => {
              setValue(newValue);
              onEditorChange(newValue, editor as any);
            }}
            initialValue={forceValue ? initialValue : value}
          >
            {withToolbar && (
              <div className="editor-toolbar">{renderNode(Div, withToolbar, { editor })}</div>
            )}
            <div className="editor-content-wrapper">
              <WrapperComponent
                className={cl('editor-editable-plugins')}
                {...WrapperComponentProps}
              >
                <DolEditable
                  ref={ref}
                  className={className}
                  editor={editor}
                  withDraggable={withDraggable}
                  {...rest}
                />
              </WrapperComponent>
              {renderNode(Div, renderRight, {})}
            </div>
            {children}
          </Slate>
        </DolEditorMainWrapper>
      </DolEditorBaseContext.Provider>
    );
  }
);

const DragOverlayContent = ({ element, ...rest }) => {
  return (
    <DragOverlayMain className="drag-overlay">
      <button>⠿</button>
    </DragOverlayMain>
  );
};
